/**
 * Image Centered Arrows
 *
 * This script positions carousel arrows to be vertically centered relative to the featured collection
 * image or product image rather than the entire carousel container.
 */

class ImageCenteredArrows {
  constructor() {
    this.instances = new Set();
    this.init();
  }

  init() {
    // Initial setup
    this.setupInstances();
    
    // Setup event listeners
    window.addEventListener('resize', this.handleResize.bind(this));
    document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
    document.addEventListener('shopify:section:unload', this.handleSectionUnload.bind(this));
    
    // Listen for tab changes
    document.addEventListener('click', this.handleTabClick.bind(this));
  }

  setupInstances() {
    // Find all carousel elements with image-centered-arrows class
    const carousels = document.querySelectorAll('.carousel--image-centered-arrows');
    
    carousels.forEach(carousel => {
      if (!this.instances.has(carousel)) {
        this.setupInstance(carousel);
      }
    });
  }

  setupInstance(carousel) {
    const imageContainer = this.findImageContainer(carousel);
    const sliderNav = carousel.querySelector('.slider-nav');
    
    if (!imageContainer || !sliderNav) return;

    // Store instance
    this.instances.add(carousel);

    // Initial positioning
    this.positionNav(carousel, imageContainer, sliderNav);
  }

  findImageContainer(carousel) {
    // Try multiple selectors to find the image container
    const selectors = [
      '.featured-collection-image',
      '.card__media',
      '.media',
      'img'
    ];

    for (const selector of selectors) {
      const element = carousel.querySelector(selector);
      if (element) return element;
    }
    return null;
  }

  positionNav(carousel, imageContainer, sliderNav) {
    try {
      const imageRect = imageContainer.getBoundingClientRect();
      const carouselRect = carousel.getBoundingClientRect();

      // Calculate the top position based on the image container's center
      const topPosition = imageRect.top - carouselRect.top + (imageRect.height / 2);
      
      // Update the position
      sliderNav.style.top = `${topPosition}px`;
      sliderNav.classList.remove('hidden');
    } catch (error) {
      console.warn('Error positioning nav:', error);
    }
  }

  updateAllInstances() {
    this.instances.forEach(carousel => {
      const imageContainer = this.findImageContainer(carousel);
      const sliderNav = carousel.querySelector('.slider-nav');
      
      if (imageContainer && sliderNav) {
        this.positionNav(carousel, imageContainer, sliderNav);
      }
    });
  }

  handleResize() {
    // Debounce resize events
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    this.resizeTimeout = setTimeout(() => {
      this.updateAllInstances();
    }, 100);
  }

  handleSectionLoad(event) {
    const container = event.target;
    const carousel = container.querySelector('.carousel--image-centered-arrows');
    if (carousel && !this.instances.has(carousel)) {
      this.setupInstance(carousel);
    }
  }

  handleSectionUnload(event) {
    const container = event.target;
    const carousel = container.querySelector('.carousel--image-centered-arrows');
    if (carousel) {
      this.instances.delete(carousel);
    }
  }

  handleTabClick(event) {
    // Check if the clicked element is a tab
    const tab = event.target.closest('.tablist__tab');
    if (!tab) return;

    // Wait for the tab panel to be shown
    setTimeout(() => {
      this.updateAllInstances();
    }, 100);
  }

  destroy() {
    // Cleanup event listeners
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
    document.removeEventListener('click', this.handleTabClick);
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    this.instances.clear();
  }
}

// Initialize the functionality
const initImageCenteredArrows = () => {
  window.imageCenteredArrows = new ImageCenteredArrows();
};

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageCenteredArrows);
} else {
  initImageCenteredArrows();
}
